import { baseSuccessToastMessage } from "@/primevue/init";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import { api, types } from "@/lib/openapi";
import {
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";
import { useCurrentEntityManager } from "./entity-store";

export type Process = types.Process;
export type ProcessInTree = types.ProcessInTree;

export const useProcessesStore = defineStore("processes", () => {
    const { sync } = useWorktreeStore();
    const { isFetching: isWorktreeBeingFetched } = useWorktreeManager();
    const { product, isFetching: isEntityBeingFetched } =
        useCurrentEntityManager();
    const toast = useToast();
    const isLoading = ref(false);

    // Note, we're not fetching the processes, we're using the worktree, which
    // also contains the processes (just less detailed), to avoid another
    // fetch.
    const processes = computed<ProcessInTree[]>(() => {
        if (!product.value) {
            return [];
        }

        return product.value.processes.sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    });

    /**
     * Create a new process. When no productId is provided,
     * uses the current product.
     */
    const create = async (
        name: string,
        productId?: string,
        description?: Blob
    ) => {
        isLoading.value = true;
        const { data, response } =
            await api.startProcessProcessMgtCreateProcessStartPost({
                body: {
                    product_id: productId ?? product.value?.identity ?? "",
                    name: name,
                    description,
                },
            });
        if (response.ok) {
            await sync();
        } else {
            toast.add({
                severity: "error",
                summary: "Could not create process",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    /**
     * Deletes the given Process.
     */
    async function remove(
        processId: string,
        opts: { showToast: boolean } = { showToast: true }
    ) {
        isLoading.value = true;
        const { response } =
            await api.startProcessProcessMgtDeleteProcessStartPost({
                body: { process_id: processId },
            });
        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Process ${processId} deleted`,
                });
            }
            await sync();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete process: " + processId,
                    detail: "Server returned " + response.status,
                });
            }
        }
        isLoading.value = false;
    }

    return {
        remove,
        create,
        processes,
        isLoading,
        isFetching: computed(
            () => isWorktreeBeingFetched.value || isEntityBeingFetched.value
        ),
    };
});

export const useProcessesManager = () => storeToRefs(useProcessesStore());
